// libs
import { useDispatch } from 'react-redux'
import { useLazyQuery } from '@apollo/client/react'
// hooks
import { useRiskModuleData } from 'hooks/useRiskModuleData'
// graphql
import { OBJECT_ENTITIES_QUERY } from 'graphql/objects/queries'
import { OBJECT_SECURITIES_QUERY } from 'graphql/objectSecurity/queries'
import { SECURITY_PROFILES_QUERY } from 'graphql/securityProfiles/queries'
import { COMPANY_RISK_RESPONSE_TYPES_QUERY } from 'graphql/company/queries'
// types
import { IObjectEntity, IObjectSecurity, IResponseTypesSet, ISecurityProfile } from 'types/graphql'
// redux
import { setResponseTypes } from 'store/reducers/riskTemplates'
import { setObjectEntities as setObjectEntitiesAction } from 'store/reducers/objectEntities'
import { setObjectSecurities as setObjectSecuritiesAction } from 'store/reducers/objectSecurities'
import { setSecurityProfiles as setSecurityProfilesAction } from 'store/reducers/securityProfiles'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'
import { omitTypenameFromObj } from 'utils/graphql'

export const useInitialData = () => {
  const dispatch = useDispatch()

  const { setRiskViews, setRiskTemplates } = useRiskModuleData()

  const [getSecurityProfiles] = useLazyQuery(SECURITY_PROFILES_QUERY, {
    fetchPolicy: 'no-cache'
  })

  const [getObjectSecurities] = useLazyQuery(OBJECT_SECURITIES_QUERY, {
    fetchPolicy: 'no-cache'
  })

  const [getObjectEntities] = useLazyQuery(OBJECT_ENTITIES_QUERY, {
    fetchPolicy: 'no-cache'
  })

  const [getCompanyResponseTypes] = useLazyQuery(COMPANY_RISK_RESPONSE_TYPES_QUERY, {
    errorPolicy: 'ignore'
  })

  const setSecurityProfiles = async () => {
    try {
      const data = await getSecurityProfiles({
        variables: { pagination: { pageTake: 1000, pageSkip: 0 } }
      })

      const securityProfiles: ISecurityProfile[] = get(data, 'data.securityProfiles.data', [])

      dispatch(setSecurityProfilesAction(securityProfiles))
    } catch (e) {
      handleError(e, 'useInitialData.setSecurityProfiles')
    }
  }

  const setObjectSecurities = async () => {
    try {
      const data = await getObjectSecurities({
        variables: { pagination: { pageTake: 1000, pageSkip: 0 } }
      })

      const objectSecurities: IObjectSecurity[] = get(data, 'data.objectSecurities.data', [])

      dispatch(setObjectSecuritiesAction(objectSecurities))
    } catch (e) {
      handleError(e, 'useInitialData.setObjectSecurities')
    }
  }

  const setObjectEntities = async () => {
    try {
      const data = await getObjectEntities({
        variables: { pagination: { pageTake: 10, pageSkip: 0 } }
      })

      const objectEntities: IObjectEntity[] = get(data, 'data.objectEntities.data', [])

      dispatch(setObjectEntitiesAction(objectEntities))
    } catch (e) {
      handleError(e, 'useInitialData.setObjectEntities')
    }
  }

  const setCompanyResponseTypes = async () => {
    try {
      const data = await getCompanyResponseTypes()

      const responseTypeSets: IResponseTypesSet[] = omitTypenameFromObj(
        get(data, 'data.getCompanyRiskResponseTypes.responseTypesSets', [])
      )

      dispatch(setResponseTypes(responseTypeSets))
    } catch (e) {
      handleError(e, 'useInitialData.setCompanyResponseTypes')
    }
  }

  return {
    setRiskViews,
    setRiskTemplates,
    setObjectEntities,
    setSecurityProfiles,
    setObjectSecurities,
    setCompanyResponseTypes
  }
}
